
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SimulationResult } from './types';
import { motion } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';

// Import our component files
import { ComplianceSummaryCards } from './results/ComplianceSummaryCards';
import { ComplianceProgressBar } from './results/ComplianceProgressBar';
import { ExecutiveSummary } from './results/ExecutiveSummary';
import { ActionsTable } from './results/ActionsTable';
import { WarningMessage } from './results/WarningMessage';
import { ComplianceDetails } from './results/ComplianceDetails';
import { ActionButtons } from './results/ActionButtons';

interface SimulationResultsProps {
  results: SimulationResult;
  onHide: () => void;
}

export const SimulationResults = ({ results, onHide }: SimulationResultsProps) => {
  // Parse details string into actionable items
  const actionItems = results.actionsList || [
    "Updated team ownership information",
    "Fixed compliance documentation",
    "Reset inactive team channels",
    "Archived unused content",
    "Added missing security contacts"
  ];
  
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="w-full"
    >
      <Card className="border-primary shadow-md max-h-[80vh] flex flex-col overflow-hidden">
        <CardHeader className="py-3 px-4 flex flex-row items-center justify-between space-y-0 bg-primary/10 border-b shrink-0">
          <CardTitle className="text-base flex items-center">
            <FileCheck className="h-4 w-4 mr-2" />
            Simulation Results - {results.policyName}
          </CardTitle>
          <Button
            variant="ghost"
            size="icon"
            onClick={onHide}
            className="h-6 w-6"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <ScrollArea className="flex-grow overflow-auto">
          <div className="flex flex-col min-h-0">
            {/* Summary Section */}
            <div className="p-4 pb-0">
              <ComplianceSummaryCards 
                actionsPerformed={results.actionsPerformed}
                complianceImprovement={results.complianceImprovement}
                issuesDetected={results.issuesDetected}
              />
            </div>
            
            {/* Progress Bar Section */}
            <ComplianceProgressBar 
              complianceBefore={results.complianceBefore || 0}
              complianceImprovement={results.complianceImprovement}
            />
            
            {/* Executive Summary */}
            <ExecutiveSummary 
              summary={results.summary}
              executedAt={results.executedAt}
              duration={results.duration}
              entitiesProcessed={results.entitiesProcessed}
            />
            
            {/* Actions Taken Table */}
            <ActionsTable actionItems={actionItems} />
            
            {/* Warning Messages */}
            <WarningMessage issuesDetected={results.issuesDetected} />
            
            {/* Additional Details Section */}
            <ComplianceDetails 
              complianceBefore={results.complianceBefore || 0}
              complianceAfter={results.complianceAfter || 0}
              complianceImprovement={results.complianceImprovement}
            />
          </div>
        </ScrollArea>
        
        {/* Action Buttons - Fixed at the bottom */}
        <ActionButtons onHide={onHide} />
      </Card>
    </motion.div>
  );
};
